import { useMemo } from "react";
import { useQuery } from "urql";
import { graphql } from "@acto/gql";
import { Customer } from "@acto/taskpane/core/domain";
import { mapClientToCustomer } from "@acto/taskpane/core/data-access/customers/mapClientToCustomer";
import type { SingleDataAccessResult } from "@acto/taskpane/core/data-access/customers/useCustomerById";

const ClientByIdWithActivityYearsQuery = graphql(`
  query GetClientByIdWithActivityYears($id: ID!) {
    clients(first: 1, filter: { id: $id }) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
          orderYears
          offerYears
          noteYears
        }
      }
    }
  }
`);

export function useCustomerWithActivityYears(id: string): SingleDataAccessResult<Customer> {
  const [result, reexecuteQuery] = useQuery({
    query: ClientByIdWithActivityYearsQuery,
    variables: { id },
    pause: !id,
    requestPolicy: "cache-first",
  });

  const customer = useMemo(() => {
    const node = result.data?.clients?.edges?.[0]?.node;
    return node ? mapClientToCustomer(node) : undefined;
  }, [result.data]);

  const refetch = () => reexecuteQuery({ requestPolicy: "network-only" });

  return {
    data: customer,
    isFetching: result.fetching,
    isPending: result.fetching && !result.data,
    error: result.error,
    refetch,
  };
}
